<template>
  <NoteSection label="Operative Note">
    <v-row>
      <v-col
        col="12"
        class="d-flex align-center justify-center"
      >
        <p>
          Photograph a signed document verifying that consent was obtained
          for this procedure and indicating the party from which consent was
          obtained:
        </p>
      </v-col>
    </v-row>
    <NoteSection
      label="Consent Document Photo(s)"
      required
      :highlight-required="isConsentPhotoIncomplete"
      :form-validated="formValidated"
    >
      <div class="note-section col-sm-12">
        <v-expand-transition>
          <div v-if="!hasAmniosAttachments">
            <alert color="error">
              There should be at least one attachment to pass validation.
              <checkbox required/>
            </alert>
          </div>
        </v-expand-transition>
      </div>

      <AmnioticAttachments
        :attachment-type.sync="attachmentType"
        attachment-type-id="3"
        :patient-attachments.sync="computedPatientAttachments"
        :patient-id="patientId"
        :default-attachment-type="'amnio-consent'"
        :provided-buckets="consentBucket"
      />
    </NoteSection>

    <v-row>
      <v-col class="col-sm-5 d-flex">
        <NoteSection
          label="Consent"
          custom-class="flex-grow-1 d-flex flex-column"
          required
          :highlight-required="isConsentIncomplete"
          :form-validated="formValidated"
        >
          <radio-buttons
            v-model="selectedConsent"
            label="Consent obtained from:"
            :items="consentOptions"
            clearable
            column
            required
          ></radio-buttons>
        </NoteSection>
      </v-col>
      <v-col class="col-sm-7 d-flex">
        <NoteSection
          label="Wound Dimensions"
          custom-class="flex-grow-1 d-flex flex-column"
          required
          :highlight-required="isWoundDimensionsIncomplete"
          :form-validated="formValidated"
        >
          <p>Input the dimensions of the wound base (after any debridement was performed, if applicable):</p>
          <text-field
            v-model="wound_treatments_amnios.dim_length"
            class="col-sm-12"
            label="Length (cm)"
            numeric
            :mask="measurementMask"
            rules="numberRange(0,100)"
            label-top
            required
            @blur="wound_treatments_amnios.dim_length =
            (wound_treatments_amnios.dim_length && wound_treatments_amnios.dim_length >= 0 && wound_treatments_amnios.dim_length <= 100)
              ? Number(wound_treatments_amnios.dim_length || 0).toFixed(1)
              : null"
          ></text-field>
          <text-field
            v-model="wound_treatments_amnios.dim_width"
            class="col-sm-12"
            label="Width (cm)"
            numeric
            :mask="measurementMask"
            rules="numberRange(0,100)"
            label-top
            required
            @blur="wound_treatments_amnios.dim_width =
            (wound_treatments_amnios.dim_width && wound_treatments_amnios.dim_width >= 0 && wound_treatments_amnios.dim_width <= 100)
              ? Number(wound_treatments_amnios.dim_width || 0).toFixed(1)
              : null"
          ></text-field>
          <text-field
            v-model="wound_treatments_amnios.dim_depth"
            class="col-sm-12"
            label="Depth (cm)"
            numeric
            allow-utd
            :mask="wound_treatments_amnios.dim_depth !== 'UTD' ? measurementMask : false"
            rules="numberRangeUTD(0, 100)"
            label-top
            required
            :append-icon="!wound_treatments_amnios.dim_depth ? icons.mdiTimelineQuestion : null"
            @click:append="setDimDepth('UTD')"
            @click:clear="setDimDepth(null)"
            @blur="postDimDepth()"
          ></text-field>
        </NoteSection>
      </v-col>
    </v-row>


    <NoteSection
      label="Fixation"
      required
      :highlight-required="isFixationIncomplete"
      :form-validated="formValidated"
    >
      <check-boxes
        v-model="selectedFixations"
        label="Indicate how fixation was achieved:"
        :items="fixationsItems"
        class="col-sm-12 mt--2 pb-3"
        columns="4"
        return-object
        multiple
        clearable
        :spaced="'between'"
        required
      />
    </NoteSection>

    <NoteSection
      label="Application Notes"
      required
      :highlight-required="isApplicationNotesIncomplete"
      :form-validated="formValidated"
    >
      <p class="py-2">
        Explain how the amniotic tissue graft was applied. Be
        sure to include disclosures regarding possible risks and
        complications associated with the application:
      </p>
      <v-spacer></v-spacer>
      <text-area
        v-model="wound_treatments_amnios.application_notes"
        label="Enter application notes..."
        class="col-sm-12"
        required
        counter="2200"
        maxlength="2200"
        ai
        @click:prepend="generateOperativeNote()"
      />
    </NoteSection>
  </NoteSection>
</template>

<script>
import {
  mdiTimelineQuestion,
} from '@mdi/js'
import AmnioticAttachments from './AmnioticAttachments.vue'

export default {
  components: {
    AmnioticAttachments,
  },
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    patientId: {
      type: String,
      default: null,
    },
    patientAttachments: {
      type: Array,
      default: () => [],
    },
    patientAmnioConsent: {
      type: String,
      default: '',
    },
    woundTreatmentAmnioFixations: {
      type: Array,
      default: () => [],
    },
    // Form validation state passed down
    formValidated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      wound_treatments_amnios: this.value,
      consentOptions: [
        { title: 'Family' },
        { title: 'Patient' },
        { title: 'Patient conservator' },
        { title: 'Patient spouse' },
        { title: 'Two doctors' },
      ],
      icons: {
        mdiTimelineQuestion,
      },
      attachmentType: 'consent',
      consentBucket: [],
      selectedFixations: [],
      measurementMask: [/[0-9|.]/, /[0-9|.]/, /[0-9|.]/, /[0-9|.]/, /[0-9|.]/],
    }
  },
  computed: {
    fixationsItems() {
      return this.$store.getters['baseData/amnioFixations']
    },
    computedPatientAttachments() {
      return this.patientAttachments
    },
    hasAmniosAttachments() {
      return this.computedPatientAttachments
        && this.computedPatientAttachments.length
        && this.computedPatientAttachments.some(att => att.attachment_type === 'amnio-consent')
    },
    selectedConsent: {
      get() {
        return this.patientAmnioConsent
      },
      set(value) {
        this.$emit('update:patientAmnioConsent', value)
      },
    },

    // Evaluates incomplete states for section highlights
    isConsentPhotoIncomplete() {
      return !this.hasAmniosAttachments
    },
    isConsentIncomplete() {
      const consent = this.selectedConsent
      if (!consent) return true
      if (Array.isArray(consent) && consent.length === 0) return true
      return false
    },
    isWoundDimensionsIncomplete() {
      const hasLength = this.wound_treatments_amnios?.dim_length !== null && this.wound_treatments_amnios?.dim_length !== undefined && String(this.wound_treatments_amnios.dim_length).trim() !== ''
      const hasWidth = this.wound_treatments_amnios?.dim_width !== null && this.wound_treatments_amnios?.dim_width !== undefined && String(this.wound_treatments_amnios.dim_width).trim() !== ''
      const hasDepth = this.wound_treatments_amnios?.dim_depth !== null && this.wound_treatments_amnios?.dim_depth !== undefined && String(this.wound_treatments_amnios.dim_depth).trim() !== ''

      return !hasLength || !hasWidth || !hasDepth
    },
    isFixationIncomplete() {
      return !this.selectedFixations || this.selectedFixations.length === 0
    },
    isApplicationNotesIncomplete() {
      return !this.wound_treatments_amnios?.application_notes || !this.wound_treatments_amnios.application_notes.trim()
    },
  },
  watch: {
    wound_treatments_amnios: {
      deep: true,
      handler() {
        this.$emit('input', this.wound_treatments_amnios)
      },
    },
    selectedFixations: {
      deep: true,
      handler() {
        // Here we emit the value in a format the wound_treatment_amnios_fixations accepts
        this.$emit('update:woundTreatmentAmnioFixations', this.selectedFixations.map(fixation => (
          {
            treatment_id: this.wound_treatments_amnios.treatment_id,
            id: fixation.id,
          }
        )))
      },
    },
  },
  created() {
    this.consentBucket = [this.$store.getters['baseData/amnioticBucketTypeFromValue']('amnio-consent')]
  },
  async mounted() {
    // Selected fixations needs a format based on baseData/amnioFixations with id and title
    // but we receive fixation_id and treatment_id from the parent component, so we map it.
    const fixationItems = await this.$store.getters['baseData/amnioFixations']

    this.selectedFixations = this.woundTreatmentAmnioFixations
      .map(f => fixationItems.find(fI => f.id === fI.id))
      .filter(Boolean)
  },
  methods: {
    generateOperativeNote() {
      let noteParts = ''

      // Consent
      noteParts += this.generateConsentText()

      // Wound dimensions
      noteParts += this.generateWoundDimensionsText()
      noteParts += ' wound bed was prepared for graft application by creating a clean wound by removal of any debris or contaminants. The'
      noteParts += `${this.$custom.randomString(
        [
          'wound base',
          "wound's base",
          'base of the wound',
        ],
      )}`
      noteParts += 'was '
      noteParts += `${this.$custom.randomString([
        'washed',
        'cleansed',
      ]).trim()}`

      noteParts += `${this.$custom.randomString([
        'using',
        'by means of',
        'with',
        'with the use of',
        'by the use of',
        'by employing',
        'utilizing',
      ])}`
      noteParts += 'normal saline-moistened gauze. '
      noteParts += 'With a gloved hand, the sterile amniotic graft was applied within the wound bed.  Fixation was achieved with '

      // Fixations
      const fixationsTitles = this.selectedFixations.map(f => f?.title)
      noteParts += this.$custom.toListCommaAnd(
        fixationsTitles.length ? fixationsTitles : '<NO FIXATION SELECTED>',
        null,
        'none'
      ).trim()
      noteParts += '. Proper dressing was placed to secure the entire wound.'

      this.wound_treatments_amnios.application_notes = noteParts
    },
    generateConsentText() {
      const consentMap = {
        // eslint-disable-next-line quote-props
        'Family': "the patient's family",
        // eslint-disable-next-line quote-props
        'Patient': 'the patient',
        'Patient conservator': "the patient's conservator",
        'Patient spouse': "the patient's spouse",
        'Two doctors': 'two doctors',
      }

      const rawConsent = this.selectedConsent
      const selectedConsent = Array.isArray(rawConsent) ? rawConsent[0] : rawConsent
      if (!selectedConsent) return 'After consent was obtained from <CONSENT SOURCE UNSPECIFIED>, '

      const consentText = consentMap[selectedConsent] || selectedConsent.toLowerCase()

      return `After consent was obtained from ${consentText}, `
    },
    generateWoundDimensionsText() {
      let woundDimensionsText = ''

      const dimLength = this.wound_treatments_amnios.dim_length
      const dimWidth = this.wound_treatments_amnios.dim_width
      if (dimLength && dimWidth) {
        let area = dimLength * dimWidth

        area = area.toFixed(2)

        // For leading 0
        if (area.startsWith('.')) {
          area = `0${area}`
        }
        woundDimensionsText += 'a measurement of the'
        woundDimensionsText += `${this.$custom.randomString(
          [
            'wound base',
            "wound's base",
            'base of the wound',
          ],
        )}`

        woundDimensionsText += 'was'

        woundDimensionsText += `${this.$custom.randomString(
          [
            'taken',
            'recorded',
            'documented',
            'noted',
            'determined',
          ],
        )}`

        woundDimensionsText += `and was measured to be ${area}`
        woundDimensionsText += ' cm².  The'
      } else {
        woundDimensionsText += 'the'
      }

      return woundDimensionsText
    },
    setDimDepth(value) {
      this.wound_treatments_amnios = {
        ...this.wound_treatments_amnios,
        dim_depth: value,
      }
    },
    postDimDepth() {
      this.$nextTick(() => {
        if (this.wound_treatments_amnios.dim_depth !== 'UTD') {
          this.wound_treatments_amnios.dim_depth =
            (this.wound_treatments_amnios.dim_depth && this.wound_treatments_amnios.dim_depth >= 0 && this.wound_treatments_amnios.dim_depth <= 100)
              ? Number(this.wound_treatments_amnios.dim_depth || 0).toFixed(1)
              : null
        }
      })
    },
  },
}
</script>

<style>
.carousel-rounded {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
</style>
